import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Layout } from "./components/Layout";
import { PwaInstallPrompt } from "./components/PwaInstallPrompt";
import { Card, Skeleton } from "./components/ui";
import { type ApiUser, fetchMe } from "./lib/api";
import { type RouteImporter, prefetchRoute, routeKeyFromPath } from "./lib/route-prefetch";
import { LoginPage } from "./pages/Login";
import { TodayPage } from "./pages/Today";

const importAdminUsers = () =>
  import("./pages/AdminUsers").then((mod) => ({ default: mod.AdminUsersPage }));
const importAdminApiKeys = () =>
  import("./pages/AdminApiKeys").then((mod) => ({ default: mod.AdminApiKeysPage }));
const importAdminLineChannels = () =>
  import("./pages/AdminLineChannels").then((mod) => ({ default: mod.AdminLineChannelsPage }));
const importAdminImapAccounts = () =>
  import("./pages/AdminImapAccounts").then((mod) => ({ default: mod.AdminImapAccountsPage }));
const importAdminTouki = () =>
  import("./pages/AdminTouki").then((mod) => ({ default: mod.AdminToukiPage }));
const importAdminUnlinkedMessages = () =>
  import("./pages/UnlinkedMessages").then((mod) => ({ default: mod.UnlinkedMessagesPage }));
const importAuditLogs = () =>
  import("./pages/AuditLogs").then((mod) => ({ default: mod.AuditLogsPage }));
const importCaseDetail = () =>
  import("./pages/CaseDetail").then((mod) => ({ default: mod.CaseDetailPage }));
const importCases = () => import("./pages/Cases").then((mod) => ({ default: mod.CasesPage }));
const importConsent = () => import("./pages/Consent").then((mod) => ({ default: mod.ConsentPage }));
const importAllMessages = () => import("./pages/AllMessages");
const importCustomerDetail = () =>
  import("./pages/CustomerDetail").then((mod) => ({ default: mod.CustomerDetailPage }));
const importCustomers = () =>
  import("./pages/Customers").then((mod) => ({ default: mod.CustomersPage }));
const importMessages = () =>
  import("./pages/Messages").then((mod) => ({ default: mod.MessagesPage }));
const importSettings = () =>
  import("./pages/Settings").then((mod) => ({ default: mod.SettingsPage }));
const importAdminSurveys = () =>
  import("./pages/AdminSurveys").then((mod) => ({ default: mod.AdminSurveysPage }));
const importSurvey = () => import("./pages/Survey").then((mod) => ({ default: mod.SurveyPage }));
const importTasks = () => import("./pages/Tasks").then((mod) => ({ default: mod.TasksPage }));

const AdminUsersPage = lazy(importAdminUsers);
const AdminApiKeysPage = lazy(importAdminApiKeys);
const AdminLineChannelsPage = lazy(importAdminLineChannels);
const AdminImapAccountsPage = lazy(importAdminImapAccounts);
const AdminToukiPage = lazy(importAdminTouki);
const AdminUnlinkedMessagesPage = lazy(importAdminUnlinkedMessages);
const AuditLogsPage = lazy(importAuditLogs);
const CaseDetailPage = lazy(importCaseDetail);
const CasesPage = lazy(importCases);
const ConsentPage = lazy(importConsent);
const AllMessages = lazy(importAllMessages);
const CustomerDetailPage = lazy(importCustomerDetail);
const CustomersPage = lazy(importCustomers);
const MessagesPage = lazy(importMessages);
const SettingsPage = lazy(importSettings);
const AdminSurveysPage = lazy(importAdminSurveys);
const SurveyPage = lazy(importSurvey);
const TasksPage = lazy(importTasks);

export const routeImporters: Record<string, RouteImporter> = {
  "/admin/users": importAdminUsers,
  "/admin/api-keys": importAdminApiKeys,
  "/admin/line-channels": importAdminLineChannels,
  "/admin/imap-accounts": importAdminImapAccounts,
  "/admin/touki": importAdminTouki,
  "/admin/messages/unlinked": importAdminUnlinkedMessages,
  "/audit-logs": importAuditLogs,
  "/cases": importCases,
  "/cases/:caseId": importCaseDetail,
  "/cases/:caseId/messages": importMessages,
  "/customers": importCustomers,
  "/customers/:customerId": importCustomerDetail,
  "/follow-ups": importCases,
  "/messages": importAllMessages,
  "/oauth/consent": importConsent,
  "/pipeline": importCases,
  "/settings": importSettings,
  "/admin/surveys": importAdminSurveys,
  "/tasks": importTasks,
};

const INITIAL_PREFETCH_ROUTES = ["/cases", "/customers", "/messages"] as const;

type AuthState =
  | { status: "loading" }
  | { status: "guest" }
  | { status: "authenticated"; user: ApiUser };

/**
 * App ルート。
 *
 * Phase 2-3-min:
 * - 起動時に GET /api/me で session を解決
 * - 未ログイン → /login にリダイレクト
 * - ログイン後は Layout 配下のページに遷移可能
 *
 * Phase 2-3c 以降で書き込みフロー / より細かいルートガードを追加する。
 */
export function App(): JSX.Element {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((res) => {
        if (cancelled) return;
        if (res.user === null) {
          setAuth({ status: "guest" });
        } else {
          setAuth({ status: "authenticated", user: res.user });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAuth({ status: "guest" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoggedIn = useCallback((user: ApiUser) => {
    setAuth({ status: "authenticated", user });
  }, []);

  const handleLoggedOut = useCallback(() => {
    setAuth({ status: "guest" });
  }, []);

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    for (const path of INITIAL_PREFETCH_ROUTES) {
      prefetchAppRoute(path);
    }
  }, [auth.status]);

  if (auth.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-slate-500">読み込み中...</p>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* 面談アンケートの公開フォーム。非認証・Layout 外 (見込み客がスマホで回答)。 */}
        <Route
          path="/survey/:token"
          element={
            <LazyPage>
              <SurveyPage />
            </LazyPage>
          }
        />
        <Route
          path="/login"
          element={
            auth.status === "authenticated" ? (
              <Navigate to="/" replace />
            ) : (
              <LoginPage onLoggedIn={handleLoggedIn} />
            )
          }
        />
        <Route
          element={
            auth.status === "authenticated" ? (
              <Layout
                user={auth.user}
                onLoggedOut={handleLoggedOut}
                onPrefetchRoute={prefetchAppRoute}
              />
            ) : (
              <RedirectToLogin />
            )
          }
        >
          <Route path="/" element={<TodayPage />} />
          <Route path="/today" element={<TodayPage />} />
          <Route
            path="/tasks"
            element={
              <LazyPage>
                <TasksPage />
              </LazyPage>
            }
          />
          <Route
            path="/cases"
            element={
              <LazyPage>
                <CasesPage />
              </LazyPage>
            }
          />
          <Route
            path="/cases/:caseId"
            element={
              <LazyPage>
                <CaseDetailPage />
              </LazyPage>
            }
          />
          <Route
            path="/cases/:caseId/messages"
            element={
              <LazyPage>
                <MessagesPage />
              </LazyPage>
            }
          />
          <Route path="/follow-ups" element={<Navigate to="/cases?view=follow-ups" replace />} />
          <Route path="/pipeline" element={<Navigate to="/cases?view=pipeline" replace />} />
          <Route
            path="/customers"
            element={
              <LazyPage>
                <CustomersPage />
              </LazyPage>
            }
          />
          <Route
            path="/customers/:customerId"
            element={
              <LazyPage>
                <CustomerDetailPage />
              </LazyPage>
            }
          />
          <Route
            path="/messages"
            element={
              <LazyPage>
                <AllMessages />
              </LazyPage>
            }
          />
          <Route
            path="/settings"
            element={
              auth.status === "authenticated" ? (
                <LazyPage>
                  <SettingsPage user={auth.user} />
                </LazyPage>
              ) : (
                <RedirectToLogin />
              )
            }
          />
          <Route
            path="/audit-logs"
            element={
              auth.status === "authenticated" ? (
                <LazyPage>
                  <AuditLogsPage user={auth.user} />
                </LazyPage>
              ) : (
                <RedirectToLogin />
              )
            }
          />
          <Route
            path="/admin/users"
            element={
              auth.status === "authenticated" && auth.user.role === "admin" ? (
                <LazyPage>
                  <AdminUsersPage />
                </LazyPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              auth.status === "authenticated" && auth.user.role === "admin" ? (
                <LazyPage>
                  <AdminApiKeysPage />
                </LazyPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/line-channels"
            element={
              auth.status === "authenticated" && auth.user.role === "admin" ? (
                <LazyPage>
                  <AdminLineChannelsPage />
                </LazyPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/imap-accounts"
            element={
              auth.status === "authenticated" && auth.user.role === "admin" ? (
                <LazyPage>
                  <AdminImapAccountsPage />
                </LazyPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/touki"
            element={
              auth.status === "authenticated" && auth.user.role === "admin" ? (
                <LazyPage>
                  <AdminToukiPage />
                </LazyPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/messages/unlinked"
            element={
              auth.status === "authenticated" && auth.user.role === "admin" ? (
                <LazyPage>
                  <AdminUnlinkedMessagesPage />
                </LazyPage>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/surveys"
            element={
              auth.status === "authenticated" ? (
                <LazyPage>
                  <AdminSurveysPage />
                </LazyPage>
              ) : (
                <RedirectToLogin />
              )
            }
          />
          {/* OAuth consent は Layout の外 (ボトムタブ無しの専用画面) */}
        </Route>
        <Route
          path="/oauth/consent"
          element={
            auth.status === "authenticated" ? (
              <LazyPage>
                <ConsentPage />
              </LazyPage>
            ) : (
              <RedirectToLogin />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <PwaInstallPrompt />
    </>
  );
}

function prefetchAppRoute(path: string): void {
  const key = routeKeyFromPath(path);
  const importer = routeImporters[key] ?? routeImporterFromDynamicPath(key);
  if (importer === undefined) return;
  prefetchRoute(key, importer);
}

function routeImporterFromDynamicPath(path: string): RouteImporter | undefined {
  if (/^\/cases\/[^/]+\/messages$/.test(path)) return routeImporters["/cases/:caseId/messages"];
  if (/^\/cases\/[^/]+$/.test(path)) return routeImporters["/cases/:caseId"];
  if (/^\/customers\/[^/]+$/.test(path)) return routeImporters["/customers/:customerId"];
  return undefined;
}

function RedirectToLogin(): JSX.Element {
  const location = useLocation();
  return <Navigate to="/login" replace state={{ from: location }} />;
}

function LazyPage({ children }: { children: JSX.Element }): JSX.Element {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

function PageSkeleton(): JSX.Element {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      {[0, 1, 2].map((key) => (
        <Card key={key} className="space-y-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </Card>
      ))}
    </div>
  );
}
